import { type ReactNode } from "react";

import { Spinner } from "@/shared/foundation/primitives";

import styles from "./PageStatus.module.css";

/**
 * A boards page whose content is on its way: a spinner in the middle of the
 * page, in the same place from the first chunk download to the last request.
 */
export function PageLoading({ label }: { label: string }) {
  return (
    <div className={styles.status}>
      <Spinner size="lg" aria-label={label} className={styles.spinner} />
    </div>
  );
}

interface PageMessageProps {
  icon: ReactNode;
  title: string;
  /** What happened, in a sentence or two under the title. */
  children: ReactNode;
  /** What the person can do about it, as buttons under the message. */
  actions?: ReactNode;
  /** Set when the message reports a failure, so it is announced. */
  role?: "alert";
}

/**
 * A boards page that has a message in place of its content: nothing to list,
 * nothing at this address, or a request that failed.
 */
export function PageMessage({
  icon,
  title,
  children,
  actions,
  role,
}: PageMessageProps) {
  return (
    <div className={styles.status} role={role}>
      <span className={styles.icon} aria-hidden="true">
        {icon}
      </span>
      <h2 className={styles.title}>{title}</h2>
      <p className={styles.body}>{children}</p>
      <div className={styles.actions}>{actions}</div>
    </div>
  );
}
